import { useEffect, useMemo, useState } from "react";
import {
    Plus,
    Shirt,
    Repeat,
    History,
    Truck,
    Trash2,
    PackageOpen,
    AlertTriangle,
    CheckCircle2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import StateBadge from "@/components/StateBadge";
import KPICard from "@/components/KPICard";
import EmptyState from "@/components/EmptyState";
import {
    TEXTILE_CATEGORY_LABELS,
    TEXTILE_STATE_LABELS,
    LAUNDRY_BATCH_STATE_LABELS,
} from "@/lib/dictionaries";

const TEXTILE_STATES = ["clean", "at_apartments", "dirty", "in_transit", "lost"];

export default function Laundry() {
    const [tab, setTab] = useState("stock");
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [providers, setProviders] = useState([]);
    const [batches, setBatches] = useState([]);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/textiles/items"),
            api.get("/textiles/summary"),
            api.get("/laundry/providers"),
            api.get("/laundry/batches"),
            api.get("/textiles/movements"),
        ])
            .then(([i, s, p, b, m]) => {
                setItems(i.data || []);
                setSummary(s.data || null);
                setProviders(p.data || []);
                setBatches(b.data || []);
                setMovements(m.data || []);
            })
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    return (
        <div className="space-y-6" data-testid="laundry-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Textil y lavandería
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Lavandería
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        Ciclos limpio/sucio, stock en tránsito y registro de pérdidas.
                    </p>
                </div>
            </div>

            {/* KPIs */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="laundry-kpis">
                    <KPICard title="Limpio" value={summary.totals.clean} suffix="unidades" icon={CheckCircle2} accent testId="kpi-clean" />
                    <KPICard title="En apartamento" value={summary.totals.at_apartments} suffix="en uso" icon={PackageOpen} testId="kpi-apt" />
                    <KPICard title="Sucio" value={summary.totals.dirty} suffix="por recoger" icon={Shirt} testId="kpi-dirty" />
                    <KPICard title="En tránsito" value={summary.totals.in_transit} suffix="con proveedor" icon={Truck} testId="kpi-transit" />
                    <KPICard title="Pérdidas" value={summary.totals.lost} suffix="histórico" icon={AlertTriangle} testId="kpi-lost" />
                </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="laundry-tabs">
                <TabBtn active={tab === "stock"} onClick={() => setTab("stock")} icon={Shirt} label="Stock" testId="tab-stock" />
                <TabBtn active={tab === "cycles"} onClick={() => setTab("cycles")} icon={Repeat} label={`Ciclos (${batches.filter((b) => b.status !== "closed").length})`} testId="tab-cycles" />
                <TabBtn active={tab === "movements"} onClick={() => setTab("movements")} icon={History} label="Movimientos" testId="tab-movements" />
                <TabBtn active={tab === "providers"} onClick={() => setTab("providers")} icon={Truck} label="Proveedores" testId="tab-providers" />
            </div>

            {loading ? (
                <div className="zentra-skeleton h-72" />
            ) : (
                <>
                    {tab === "stock" && <StockTab items={items} reload={load} summary={summary} />}
                    {tab === "cycles" && <CyclesTab batches={batches} items={items} providers={providers} reload={load} />}
                    {tab === "movements" && <MovementsTab movements={movements} items={items} />}
                    {tab === "providers" && <ProvidersTab providers={providers} reload={load} />}
                </>
            )}
        </div>
    );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition border ${
                active
                    ? "bg-zentra-primary text-white border-zentra-primary"
                    : "bg-white text-zentra-ink border-zentra-border hover:border-zentra-olive"
            }`}
        >
            <Icon size={12} /> {label}
        </button>
    );
}

// ----- STOCK TAB -----
function StockTab({ items, reload, summary }) {
    const [open, setOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(null); // item
    const [editItem, setEditItem] = useState(null); // item being edited
    const [form, setForm] = useState({ sku: "", name: "", category: "sheet", size: "", color: "", par_per_property: 0, cost_eur: 0, stock_clean: 0 });
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            await api.post("/textiles/items", { ...form, par_per_property: Number(form.par_per_property), cost_eur: Number(form.cost_eur), stock_clean: Number(form.stock_clean) });
            setOpen(false);
            setForm({ sku: "", name: "", category: "sheet", size: "", color: "", par_per_property: 0, cost_eur: 0, stock_clean: 0 });
            reload();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar este artículo? Se borrará también su historial.")) return;
        await api.delete(`/textiles/items/${id}`);
        reload();
    };

    return (
        <div className="space-y-4" data-testid="laundry-stock-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-zentra-muted">{items.length} referencias en catálogo</p>
                <button className="zentra-btn-primary" onClick={() => setOpen(true)} data-testid="new-textile-item">
                    <Plus size={14} /> Nueva referencia
                </button>
            </div>

            {summary?.par_issues?.length > 0 && (
                <div className="zentra-card-static p-4 border-amber-200 bg-amber-50/40" data-testid="par-issues">
                    <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                        <AlertTriangle size={14} /> Déficit respecto al stock objetivo ({summary.par_issues.length})
                    </p>
                    <div className="mt-2 grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {summary.par_issues.map((p) => (
                            <div key={p.id} className="flex items-center justify-between p-2 rounded-md bg-white border border-amber-200">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-zentra-ink truncate">{p.name}</p>
                                    <p className="text-[10px] font-mono-zentra text-zentra-muted">{p.sku}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-zentra-muted">{p.on_hand} / {p.par_total}</p>
                                    <p className="text-sm font-semibold text-amber-700">−{p.deficit}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {items.length === 0 ? (
                <EmptyState icon={Shirt} title="Sin referencias" description="Añade tu primera referencia textil." />
            ) : (
                <div className="zentra-card-static overflow-x-auto">
                    <table className="zentra-table min-w-[900px]" data-testid="textile-stock-table">
                        <thead>
                            <tr>
                                <th>Referencia</th>
                                <th>Categoría</th>
                                <th className="text-center">Par/propiedad</th>
                                <th className="text-center">Limpia</th>
                                <th className="text-center">Apartamento</th>
                                <th className="text-center">Sucia</th>
                                <th className="text-center">Tránsito</th>
                                <th className="text-center">Pérdidas</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it) => (
                                <tr key={it.id} data-testid={`textile-row-${it.sku}`}>
                                    <td>
                                        <p className="font-medium text-zentra-ink">{it.name}</p>
                                        <p className="text-[10px] font-mono-zentra text-zentra-muted">{it.sku} · {it.size} {it.color}</p>
                                    </td>
                                    <td className="text-sm">{TEXTILE_CATEGORY_LABELS[it.category] || it.category}</td>
                                    <td className="text-center font-mono-zentra text-xs">{it.par_per_property || 0}</td>
                                    <td className="text-center font-display font-semibold text-emerald-700">{it.stock_clean || 0}</td>
                                    <td className="text-center font-display font-semibold text-sky-700">{it.stock_at_apartments || 0}</td>
                                    <td className="text-center font-display font-semibold text-amber-700">{it.stock_dirty || 0}</td>
                                    <td className="text-center font-display font-semibold text-violet-700">{it.stock_in_transit || 0}</td>
                                    <td className="text-center font-display font-semibold text-red-700">{it.stock_lost || 0}</td>
                                    <td className="text-right">
                                        <div className="inline-flex items-center gap-2">
                                            <button
                                                className="zentra-btn-ghost text-xs"
                                                onClick={() => setEditItem(it)}
                                                data-testid={`edit-textile-${it.sku}`}
                                            >
                                                Editar
                                            </button>
                                            <button
                                                className="zentra-btn-ghost text-xs"
                                                onClick={() => setMoveOpen(it)}
                                                data-testid={`move-textile-${it.sku}`}
                                            >
                                                Mover
                                            </button>
                                            <button
                                                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50"
                                                onClick={() => remove(it.id)}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Nueva referencia textil"
                wide
                testId="textile-item-modal"
                footer={
                    <>
                        <button className="zentra-btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
                        <button type="submit" form="textile-item-form" className="zentra-btn-primary" disabled={saving} data-testid="save-textile-item">
                            {saving ? "Guardando…" : "Crear"}
                        </button>
                    </>
                }
            >
                <form id="textile-item-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="zentra-label">SKU</label>
                        <input className="zentra-input font-mono-zentra" required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="textile-sku" />
                    </div>
                    <div>
                        <label className="zentra-label">Categoría</label>
                        <select className="zentra-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                            {Object.entries(TEXTILE_CATEGORY_LABELS).map(([k, l]) => (
                                <option key={k} value={k}>{l}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Nombre</label>
                        <input className="zentra-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="textile-name" />
                    </div>
                    <div>
                        <label className="zentra-label">Talla / medida</label>
                        <input className="zentra-input" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="150x200" />
                    </div>
                    <div>
                        <label className="zentra-label">Color</label>
                        <input className="zentra-input" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="blanco" />
                    </div>
                    <div>
                        <label className="zentra-label">Par por propiedad</label>
                        <input type="number" min="0" className="zentra-input" value={form.par_per_property} onChange={(e) => setForm({ ...form, par_per_property: e.target.value })} />
                    </div>
                    <div>
                        <label className="zentra-label">Coste unitario (€)</label>
                        <input type="number" step="0.01" min="0" className="zentra-input" value={form.cost_eur} onChange={(e) => setForm({ ...form, cost_eur: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Stock inicial (limpio)</label>
                        <input type="number" min="0" className="zentra-input" value={form.stock_clean} onChange={(e) => setForm({ ...form, stock_clean: e.target.value })} />
                    </div>
                    {error && <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
                </form>
            </Modal>

            <MoveModal item={moveOpen} onClose={() => setMoveOpen(null)} onDone={reload} />

            <EditTextileModal
                item={editItem}
                onClose={() => setEditItem(null)}
                onDone={() => { setEditItem(null); reload(); }}
            />
        </div>
    );
}

function EditTextileModal({ item, onClose, onDone }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (item) {
            setForm({
                sku: item.sku || "",
                name: item.name || "",
                category: item.category || "sheet",
                size: item.size || "",
                color: item.color || "",
                par_per_property: item.par_per_property || 0,
                cost_eur: item.cost_eur || 0,
                stock_clean: item.stock_clean || 0,
                stock_at_apartments: item.stock_at_apartments || 0,
                stock_dirty: item.stock_dirty || 0,
                stock_in_transit: item.stock_in_transit || 0,
                stock_lost: item.stock_lost || 0,
                notes: item.notes || "",
            });
            setError("");
        }
    }, [item]);

    if (!item) return null;

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...form,
                par_per_property: Number(form.par_per_property) || 0,
                cost_eur: Number(form.cost_eur) || 0,
                stock_clean: Math.max(0, Number(form.stock_clean) || 0),
                stock_at_apartments: Math.max(0, Number(form.stock_at_apartments) || 0),
                stock_dirty: Math.max(0, Number(form.stock_dirty) || 0),
                stock_in_transit: Math.max(0, Number(form.stock_in_transit) || 0),
                stock_lost: Math.max(0, Number(form.stock_lost) || 0),
            };
            await api.patch(`/textiles/items/${item.id}`, payload);
            onDone();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const totalAhora = (Number(form.stock_clean) || 0) +
                       (Number(form.stock_at_apartments) || 0) +
                       (Number(form.stock_dirty) || 0) +
                       (Number(form.stock_in_transit) || 0);
    const totalOriginal = (item.stock_clean || 0) + (item.stock_at_apartments || 0) +
                          (item.stock_dirty || 0) + (item.stock_in_transit || 0);
    const diff = totalAhora - totalOriginal;

    return (
        <Modal
            open={!!item}
            onClose={onClose}
            title={`Editar · ${item.name}`}
            subtitle="Modifica el catálogo o ajusta el stock directamente en cualquier estado."
            wide
            testId="edit-textile-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose}>Cancelar</button>
                    <button type="submit" form="edit-textile-form" className="zentra-btn-primary" disabled={saving} data-testid="save-edit-textile">
                        {saving ? "Guardando…" : "Guardar cambios"}
                    </button>
                </>
            }
        >
            <form id="edit-textile-form" onSubmit={submit} className="space-y-5">
                <fieldset className="border border-zentra-border rounded-lg p-4">
                    <legend className="px-2 -ml-2 text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Datos del artículo
                    </legend>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="zentra-label">SKU</label>
                            <input className="zentra-input font-mono-zentra" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
                        </div>
                        <div>
                            <label className="zentra-label">Categoría</label>
                            <select className="zentra-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                {Object.entries(TEXTILE_CATEGORY_LABELS).map(([k, l]) => (
                                    <option key={k} value={k}>{l}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className="zentra-label">Nombre</label>
                            <input className="zentra-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                        <div>
                            <label className="zentra-label">Talla / medida</label>
                            <input className="zentra-input" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="150x200" />
                        </div>
                        <div>
                            <label className="zentra-label">Color</label>
                            <input className="zentra-input" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
                        </div>
                        <div>
                            <label className="zentra-label">Par por propiedad</label>
                            <input type="number" min="0" className="zentra-input" value={form.par_per_property} onChange={(e) => setForm({ ...form, par_per_property: e.target.value })} />
                        </div>
                        <div>
                            <label className="zentra-label">Coste unitario (€)</label>
                            <input type="number" step="0.01" min="0" className="zentra-input" value={form.cost_eur} onChange={(e) => setForm({ ...form, cost_eur: e.target.value })} />
                        </div>
                    </div>
                </fieldset>

                <fieldset className="border border-zentra-border rounded-lg p-4">
                    <legend className="px-2 -ml-2 text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Stock por estado · ajuste directo
                    </legend>
                    <p className="text-xs text-zentra-muted mb-3">
                        Edita los valores manualmente cuando hagas un recuento físico. Estos cambios se aplican sin registrar movimientos individuales.
                    </p>
                    <div className="grid grid-cols-5 gap-3">
                        <StockField label="Limpia" tone="emerald" value={form.stock_clean} onChange={(v) => setForm({ ...form, stock_clean: v })} testId="edit-stock-clean" />
                        <StockField label="En apartamento" tone="sky" value={form.stock_at_apartments} onChange={(v) => setForm({ ...form, stock_at_apartments: v })} testId="edit-stock-apt" />
                        <StockField label="Sucia" tone="amber" value={form.stock_dirty} onChange={(v) => setForm({ ...form, stock_dirty: v })} testId="edit-stock-dirty" />
                        <StockField label="En tránsito" tone="violet" value={form.stock_in_transit} onChange={(v) => setForm({ ...form, stock_in_transit: v })} testId="edit-stock-transit" />
                        <StockField label="Pérdidas" tone="red" value={form.stock_lost} onChange={(v) => setForm({ ...form, stock_lost: v })} testId="edit-stock-lost" />
                    </div>
                    <div className="mt-4 pt-3 border-t border-zentra-border flex items-center justify-between text-sm">
                        <span className="text-zentra-muted">Total en circulación (limpia + apartamento + sucia + tránsito)</span>
                        <span className="font-display font-bold text-lg text-zentra-ink">
                            {totalAhora}
                            {diff !== 0 && (
                                <span className={`ml-2 text-xs font-mono-zentra ${diff > 0 ? "text-emerald-600" : "text-red-600"}`}>
                                    {diff > 0 ? "+" : ""}{diff} vs. {totalOriginal}
                                </span>
                            )}
                        </span>
                    </div>
                </fieldset>

                {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
            </form>
        </Modal>
    );
}

function StockField({ label, tone, value, onChange, testId }) {
    const colors = {
        emerald: "text-emerald-700 border-emerald-200 focus-within:border-emerald-500",
        sky: "text-sky-700 border-sky-200 focus-within:border-sky-500",
        amber: "text-amber-700 border-amber-200 focus-within:border-amber-500",
        violet: "text-violet-700 border-violet-200 focus-within:border-violet-500",
        red: "text-red-700 border-red-200 focus-within:border-red-500",
    };
    return (
        <div className={`rounded-md border-2 ${colors[tone]} bg-white p-2.5 transition`}>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-zentra-muted">{label}</p>
            <input
                type="number"
                min="0"
                className="w-full bg-transparent border-0 outline-none font-display text-2xl font-bold mt-1"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
            />
        </div>
    );
}

// ----- MOVE MODAL -----
function MoveModal({ item, onClose, onDone }) {
    const [form, setForm] = useState({ from_state: "clean", to_state: "at_apartments", qty: 1, note: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (item) setForm({ from_state: "clean", to_state: "at_apartments", qty: 1, note: "" });
    }, [item]);

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            await api.post("/textiles/movements", {
                item_id: item.id,
                from_state: form.from_state,
                to_state: form.to_state,
                qty: Number(form.qty),
                note: form.note,
            });
            onDone();
            onClose();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (!item) return null;

    const availFrom = item[`stock_${form.from_state}`] || 0;

    return (
        <Modal
            open={!!item}
            onClose={onClose}
            title={`Mover stock · ${item.name}`}
            subtitle={`Disponible en ${TEXTILE_STATE_LABELS[form.from_state]?.label}: ${availFrom}`}
            testId="move-textile-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose}>Cancelar</button>
                    <button type="submit" form="move-form" className="zentra-btn-primary" disabled={saving} data-testid="save-textile-move">
                        {saving ? "Moviendo…" : "Confirmar"}
                    </button>
                </>
            }
        >
            <form id="move-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div>
                    <label className="zentra-label">Desde</label>
                    <select className="zentra-select" value={form.from_state} onChange={(e) => setForm({ ...form, from_state: e.target.value })}>
                        {TEXTILE_STATES.map((s) => (
                            <option key={s} value={s}>{TEXTILE_STATE_LABELS[s]?.label || s} ({item[`stock_${s}`] || 0})</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="zentra-label">Hacia</label>
                    <select className="zentra-select" value={form.to_state} onChange={(e) => setForm({ ...form, to_state: e.target.value })}>
                        {TEXTILE_STATES.filter((s) => s !== form.from_state).map((s) => (
                            <option key={s} value={s}>{TEXTILE_STATE_LABELS[s]?.label || s}</option>
                        ))}
                    </select>
                </div>
                <div className="col-span-2">
                    <label className="zentra-label">Cantidad</label>
                    <input type="number" min="1" max={availFrom} className="zentra-input" required value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="textile-move-qty" />
                </div>
                <div className="col-span-2">
                    <label className="zentra-label">Nota (opcional)</label>
                    <input className="zentra-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </div>
                {error && <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
            </form>
        </Modal>
    );
}

// ----- CYCLES TAB -----
function CyclesTab({ batches, items, providers, reload }) {
    const [newOpen, setNewOpen] = useState(false);
    const [closeBatch, setCloseBatch] = useState(null);

    const itemsById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
    const providersById = useMemo(() => Object.fromEntries(providers.map((p) => [p.id, p])), [providers]);

    const remove = async (b) => {
        const msg = b.status === "closed"
            ? "¿Eliminar este ciclo cerrado del historial?"
            : "Este ciclo está abierto. Al eliminarlo, las unidades en tránsito vuelven a 'sucia'. ¿Continuar?";
        if (!window.confirm(msg)) return;
        await api.delete(`/laundry/batches/${b.id}`);
        reload();
    };

    return (
        <div className="space-y-4" data-testid="laundry-cycles-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-zentra-muted">{batches.length} ciclos · {batches.filter((b) => b.status !== "closed").length} en curso</p>
                <button className="zentra-btn-primary" onClick={() => setNewOpen(true)} data-testid="new-batch-button">
                    <Plus size={14} /> Nuevo envío a lavandería
                </button>
            </div>

            {batches.length === 0 ? (
                <EmptyState icon={Repeat} title="Sin ciclos" description="Aún no has registrado ningún envío." />
            ) : (
                <div className="zentra-card-static overflow-x-auto">
                    <table className="zentra-table min-w-[800px]" data-testid="batches-table">
                        <thead>
                            <tr>
                                <th>Envío</th>
                                <th>Proveedor</th>
                                <th>Artículos</th>
                                <th className="text-center">Enviados</th>
                                <th className="text-center">Devueltos</th>
                                <th className="text-center">Pérdidas</th>
                                <th>Coste</th>
                                <th>Estado</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {batches.map((b) => {
                                const totals = (b.items || []).reduce(
                                    (acc, it) => ({
                                        sent: acc.sent + (it.qty_sent || 0),
                                        ret: acc.ret + (it.qty_returned || 0),
                                        lost: acc.lost + (it.qty_lost || 0),
                                    }),
                                    { sent: 0, ret: 0, lost: 0 }
                                );
                                const prov = providersById[b.provider_id];
                                return (
                                    <tr key={b.id} data-testid={`batch-row-${b.id}`}>
                                        <td>
                                            <p className="font-medium text-zentra-ink">{b.sent_date}</p>
                                            <p className="text-[10px] font-mono-zentra text-zentra-muted">{b.id.slice(0, 8)}</p>
                                        </td>
                                        <td className="text-sm">{prov?.name || "—"}</td>
                                        <td className="text-xs text-zentra-muted">
                                            {(b.items || []).slice(0, 3).map((it) => itemsById[it.item_id]?.name || it.item_name).join(", ")}
                                            {(b.items || []).length > 3 && ` +${b.items.length - 3}`}
                                        </td>
                                        <td className="text-center font-display font-semibold">{totals.sent}</td>
                                        <td className="text-center font-display font-semibold text-emerald-700">{totals.ret || "—"}</td>
                                        <td className="text-center font-display font-semibold text-red-700">{totals.lost || "—"}</td>
                                        <td className="font-mono-zentra text-xs">{b.total_cost ? `${b.total_cost.toFixed(2)} €` : "—"}</td>
                                        <td>
                                            <StateBadge value={b.status} dict={LAUNDRY_BATCH_STATE_LABELS} />
                                        </td>
                                        <td className="text-right">
                                            <div className="inline-flex items-center gap-2">
                                                {b.status !== "closed" && (
                                                    <button
                                                        className="zentra-btn-ghost text-xs"
                                                        onClick={() => setCloseBatch(b)}
                                                        data-testid={`close-batch-${b.id}`}
                                                    >
                                                        Cerrar
                                                    </button>
                                                )}
                                                <button
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50"
                                                    onClick={() => remove(b)}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <NewBatchModal open={newOpen} onClose={() => setNewOpen(false)} items={items} providers={providers} onDone={reload} />
            <CloseBatchModal batch={closeBatch} items={items} onClose={() => setCloseBatch(null)} onDone={reload} />
        </div>
    );
}

function NewBatchModal({ open, onClose, items, providers, onDone }) {
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({ provider_id: "", sent_date: today, expected_return: "", notes: "" });
    const [rows, setRows] = useState([{ item_id: "", qty_sent: 1 }]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setForm({ provider_id: providers[0]?.id || "", sent_date: today, expected_return: "", notes: "" });
            setRows([{ item_id: "", qty_sent: 1 }]);
            setError("");
        }
    }, [open, providers, today]);

    const validRows = rows.filter((r) => r.item_id && Number(r.qty_sent) > 0);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (validRows.length === 0) {
            setError("Añade al menos un artículo con cantidad mayor que 0");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await api.post("/laundry/batches", {
                provider_id: form.provider_id || null,
                sent_date: form.sent_date,
                expected_return: form.expected_return || null,
                notes: form.notes,
                items: validRows.map((r) => ({ item_id: r.item_id, qty_sent: Number(r.qty_sent) })),
            });
            onDone();
            onClose();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Nuevo envío a lavandería"
            subtitle="Las unidades pasarán de 'sucia' a 'en tránsito'."
            wide
            testId="new-batch-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose}>Cancelar</button>
                    <button type="submit" form="new-batch-form" className="zentra-btn-primary" disabled={saving} data-testid="save-new-batch">
                        {saving ? "Enviando…" : "Crear envío"}
                    </button>
                </>
            }
        >
            <form id="new-batch-form" onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="zentra-label">Proveedor</label>
                        <select className="zentra-select" value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}>
                            <option value="">— Sin asignar —</option>
                            {providers.map((p) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="zentra-label">Fecha envío</label>
                        <input type="date" className="zentra-input" value={form.sent_date} onChange={(e) => setForm({ ...form, sent_date: e.target.value })} required />
                    </div>
                    <div>
                        <label className="zentra-label">Devolución estimada</label>
                        <input type="date" className="zentra-input" value={form.expected_return} onChange={(e) => setForm({ ...form, expected_return: e.target.value })} />
                    </div>
                </div>

                <div>
                    <label className="zentra-label">Artículos a enviar</label>
                    <div className="space-y-2 mt-2">
                        {rows.map((r, idx) => {
                            const it = items.find((x) => x.id === r.item_id);
                            const max = it?.stock_dirty || 0;
                            return (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`batch-row-${idx}`}>
                                    <div className="col-span-7">
                                        <select
                                            className="zentra-select"
                                            value={r.item_id}
                                            onChange={(e) => {
                                                const next = [...rows];
                                                next[idx] = { ...next[idx], item_id: e.target.value };
                                                setRows(next);
                                            }}
                                        >
                                            <option value="">— Seleccionar artículo —</option>
                                            {items.filter((i) => (i.stock_dirty || 0) > 0).map((i) => (
                                                <option key={i.id} value={i.id}>{i.name} (sucias: {i.stock_dirty})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-3">
                                        <input
                                            type="number"
                                            min="1"
                                            max={max || undefined}
                                            className="zentra-input"
                                            placeholder="qty"
                                            value={r.qty_sent}
                                            onChange={(e) => {
                                                const next = [...rows];
                                                next[idx] = { ...next[idx], qty_sent: e.target.value };
                                                setRows(next);
                                            }}
                                        />
                                    </div>
                                    <div className="col-span-2 flex gap-1">
                                        {rows.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => setRows(rows.filter((_, i) => i !== idx))}
                                                className="zentra-btn-ghost text-xs px-2"
                                            >
                                                ×
                                            </button>
                                        )}
                                        {idx === rows.length - 1 && (
                                            <button
                                                type="button"
                                                onClick={() => setRows([...rows, { item_id: "", qty_sent: 1 }])}
                                                className="zentra-btn-ghost text-xs px-2"
                                                data-testid="add-batch-row"
                                            >
                                                + fila
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <label className="zentra-label">Notas</label>
                    <textarea className="zentra-textarea min-h-[60px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
            </form>
        </Modal>
    );
}

function CloseBatchModal({ batch, items, onClose, onDone }) {
    const today = new Date().toISOString().slice(0, 10);
    const [rows, setRows] = useState([]);
    const [returnedDate, setReturnedDate] = useState(today);
    const [totalCost, setTotalCost] = useState(0);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (batch) {
            setRows((batch.items || []).map((it) => ({ ...it, qty_returned: it.qty_sent, qty_lost: 0 })));
            setReturnedDate(today);
            setTotalCost(batch.total_cost || 0);
            setNotes(batch.notes || "");
            setError("");
        }
    }, [batch, today]);

    if (!batch) return null;

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            await api.post(`/laundry/batches/${batch.id}/close`, {
                returned_date: returnedDate,
                total_cost: Number(totalCost) || 0,
                notes,
                items: rows.map((r) => ({ item_id: r.item_id, qty_returned: Number(r.qty_returned), qty_lost: Number(r.qty_lost) })),
            });
            onDone();
            onClose();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={!!batch}
            onClose={onClose}
            title={`Cerrar ciclo · ${batch.sent_date}`}
            subtitle="Registra unidades devueltas limpias y pérdidas. El stock se actualizará automáticamente."
            wide
            testId="close-batch-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose}>Cancelar</button>
                    <button type="submit" form="close-batch-form" className="zentra-btn-primary" disabled={saving} data-testid="save-close-batch">
                        {saving ? "Cerrando…" : "Cerrar ciclo"}
                    </button>
                </>
            }
        >
            <form id="close-batch-form" onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="zentra-label">Fecha devolución</label>
                        <input type="date" className="zentra-input" value={returnedDate} onChange={(e) => setReturnedDate(e.target.value)} required />
                    </div>
                    <div>
                        <label className="zentra-label">Coste total (€)</label>
                        <input type="number" step="0.01" min="0" className="zentra-input" value={totalCost} onChange={(e) => setTotalCost(e.target.value)} />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="zentra-table">
                        <thead>
                            <tr>
                                <th>Artículo</th>
                                <th className="text-center">Enviado</th>
                                <th className="text-center">Devuelto limpio</th>
                                <th className="text-center">Pérdida</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => (
                                <tr key={r.item_id}>
                                    <td>
                                        <p className="font-medium">{r.item_name || items.find((i) => i.id === r.item_id)?.name}</p>
                                    </td>
                                    <td className="text-center font-display font-semibold">{r.qty_sent}</td>
                                    <td className="text-center">
                                        <input
                                            type="number"
                                            min="0"
                                            max={r.qty_sent}
                                            className="zentra-input text-center w-24 inline-block"
                                            value={r.qty_returned}
                                            onChange={(e) => {
                                                const next = [...rows];
                                                next[idx] = { ...next[idx], qty_returned: e.target.value };
                                                setRows(next);
                                            }}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <input
                                            type="number"
                                            min="0"
                                            max={r.qty_sent}
                                            className="zentra-input text-center w-24 inline-block"
                                            value={r.qty_lost}
                                            onChange={(e) => {
                                                const next = [...rows];
                                                next[idx] = { ...next[idx], qty_lost: e.target.value };
                                                setRows(next);
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div>
                    <label className="zentra-label">Notas</label>
                    <textarea className="zentra-textarea min-h-[60px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
                {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
            </form>
        </Modal>
    );
}

// ----- MOVEMENTS TAB -----
function MovementsTab({ movements, items }) {
    const byItem = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
    if (movements.length === 0) {
        return <EmptyState icon={History} title="Sin movimientos" description="Aún no se han registrado movimientos." />;
    }
    return (
        <div className="zentra-card-static overflow-x-auto" data-testid="movements-tab">
            <table className="zentra-table min-w-[700px]">
                <thead>
                    <tr>
                        <th>Fecha</th>
                        <th>Artículo</th>
                        <th>Desde</th>
                        <th>Hacia</th>
                        <th className="text-center">Cantidad</th>
                        <th>Nota</th>
                    </tr>
                </thead>
                <tbody>
                    {movements.map((m) => (
                        <tr key={m.id}>
                            <td className="font-mono-zentra text-xs">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                            <td className="text-sm">{byItem[m.item_id]?.name || m.item_id.slice(0, 8)}</td>
                            <td><StateBadge value={m.from_state} dict={TEXTILE_STATE_LABELS} /></td>
                            <td><StateBadge value={m.to_state} dict={TEXTILE_STATE_LABELS} /></td>
                            <td className="text-center font-display font-semibold">{m.qty}</td>
                            <td className="text-xs text-zentra-muted">{m.note || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ----- PROVIDERS TAB -----
function ProvidersTab({ providers, reload }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", contact: "", email: "", phone: "", sla_days: 2, rate_per_kg: 0, notes: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            await api.post("/laundry/providers", { ...form, sla_days: Number(form.sla_days), rate_per_kg: Number(form.rate_per_kg) });
            setOpen(false);
            setForm({ name: "", contact: "", email: "", phone: "", sla_days: 2, rate_per_kg: 0, notes: "" });
            reload();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar este proveedor?")) return;
        await api.delete(`/laundry/providers/${id}`);
        reload();
    };

    return (
        <div className="space-y-4" data-testid="laundry-providers-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-zentra-muted">{providers.length} proveedores</p>
                <button className="zentra-btn-primary" onClick={() => setOpen(true)} data-testid="new-provider-button">
                    <Plus size={14} /> Nuevo proveedor
                </button>
            </div>

            {providers.length === 0 ? (
                <EmptyState icon={Truck} title="Sin proveedores" description="Añade tu primera lavandería externa." />
            ) : (
                <div className="zentra-card-static overflow-hidden">
                    <table className="zentra-table">
                        <thead>
                            <tr>
                                <th>Proveedor</th>
                                <th>Contacto</th>
                                <th className="text-center">SLA</th>
                                <th className="text-center">€/kg</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {providers.map((p) => (
                                <tr key={p.id} data-testid={`provider-row-${p.id}`}>
                                    <td>
                                        <p className="font-medium text-zentra-ink">{p.name}</p>
                                        {p.notes && <p className="text-[11px] text-zentra-muted">{p.notes}</p>}
                                    </td>
                                    <td className="text-sm">
                                        <p>{p.contact}</p>
                                        <p className="text-xs text-zentra-muted">{p.email}</p>
                                        <p className="text-xs text-zentra-muted font-mono-zentra">{p.phone}</p>
                                    </td>
                                    <td className="text-center font-mono-zentra text-sm">{p.sla_days} días</td>
                                    <td className="text-center font-mono-zentra text-sm">{(p.rate_per_kg || 0).toFixed(2)} €</td>
                                    <td className="text-right">
                                        <button
                                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50"
                                            onClick={() => remove(p.id)}
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Nuevo proveedor de lavandería"
                wide
                testId="provider-modal"
                footer={
                    <>
                        <button className="zentra-btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
                        <button type="submit" form="provider-form" className="zentra-btn-primary" disabled={saving} data-testid="save-provider">
                            {saving ? "Guardando…" : "Crear"}
                        </button>
                    </>
                }
            >
                <form id="provider-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className="zentra-label">Nombre</label>
                        <input className="zentra-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="provider-name" />
                    </div>
                    <div>
                        <label className="zentra-label">Persona de contacto</label>
                        <input className="zentra-input" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
                    </div>
                    <div>
                        <label className="zentra-label">Email</label>
                        <input className="zentra-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div>
                        <label className="zentra-label">Teléfono</label>
                        <input className="zentra-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div>
                        <label className="zentra-label">SLA (días)</label>
                        <input type="number" min="0" className="zentra-input" value={form.sla_days} onChange={(e) => setForm({ ...form, sla_days: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Tarifa €/kg</label>
                        <input type="number" step="0.01" min="0" className="zentra-input" value={form.rate_per_kg} onChange={(e) => setForm({ ...form, rate_per_kg: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Notas</label>
                        <textarea className="zentra-textarea min-h-[60px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </div>
                    {error && <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
                </form>
            </Modal>
        </div>
    );
}
